import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Book, Shield, Menu, Search, Globe, ChevronDown, Landmark, PenTool } from 'lucide-react';

const HarvardUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Harvard University";

    return (
        <div className="min-h-screen bg-[#F8F7F5] font-serif text-[#1e1e1e] selection:bg-[#A51C30] selection:text-white border-t-[6px] border-[#A51C30]">
            {/* Functional Top Bar */}
            <div className="bg-[#1e1e1e] text-white/90 font-sans text-xs py-2 px-6 flex justify-end gap-6 uppercase tracking-wider font-bold">
                <a href="#" className="hover:text-[#A51C30]">Faculty</a>
                <a href="#" className="hover:text-[#A51C30]">Staff</a>
                <a href="#" className="hover:text-[#A51C30]">Student</a>
                <a href="#" className="hover:text-[#A51C30]">Alumni</a>
            </div>

            {/* Main Header */}
            <nav className="bg-[#A51C30] text-white px-6 py-4 sticky top-0 z-50 shadow-md">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-transparent border-2 border-white/30 rounded-sm flex items-center justify-center">
                            <Shield size={24} className="text-white fill-white" />
                        </div>
                        <span className="font-serif text-2xl font-bold tracking-wide leading-none uppercase">
                            {schoolName.split(' ')[0]} <br/>
                            <span className="text-sm font-sans font-normal tracking-widest opacity-80">{schoolName.split(' ')[1]}</span>
                        </span>
                    </div>

                    <div className="hidden lg:flex items-center gap-8 font-sans text-sm font-bold uppercase tracking-widest">
                        <div className="group relative cursor-pointer flex items-center gap-1 hover:text-white/80">
                            About <ChevronDown size={14} />
                        </div>
                        <div className="group relative cursor-pointer flex items-center gap-1 hover:text-white/80">
                            Admissions <ChevronDown size={14} />
                        </div>
                         <div className="group relative cursor-pointer flex items-center gap-1 hover:text-white/80">
                            Schools <ChevronDown size={14} />
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <Search size={20} className="cursor-pointer hover:opacity-80" />
                        <Menu size={24} className="lg:hidden cursor-pointer" />
                    </div>
                </div>
            </nav>

            {/* Hero Section - Classic */}
            <header className="relative h-[650px] bg-black">
                <img 
                    src="https://images.unsplash.com/photo-1548504769-900b70ed1c34?q=80&w=2070&auto=format&fit=crop" 
                    alt="Harvard Yard" 
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-0 w-full p-12 md:p-24 flex flex-col items-center text-center">
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 max-w-4xl leading-tight">
                        Devoted to excellence in teaching, learning, and research.
                    </h1>
                    <p className="text-white/80 text-xl font-sans mb-10 max-w-2xl">
                        Developing leaders who make a difference globally.
                    </p>
                    <Button className="bg-[#A51C30] hover:bg-[#801625] text-white font-sans font-bold uppercase tracking-widest px-10 h-14 rounded-sm text-sm">
                        Explore Harvard
                    </Button>
                </div>
            </header>

            {/* News - Magazine Style */}
            <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="flex items-center gap-4 mb-16 justify-center">
                    <div className="h-px w-16 bg-[#A51C30]"></div>
                    <h2 className="text-3xl font-serif font-bold text-center text-[#1e1e1e]">Harvard Gazette</h2>
                    <div className="h-px w-16 bg-[#A51C30]"></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div className="lg:col-span-2 group cursor-pointer">
                        <div className="aspect-[16/10] overflow-hidden mb-6 relative">
                            <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Science" />
                            <div className="absolute top-4 left-4 bg-white/90 text-[#A51C30] px-3 py-1 font-sans text-xs font-bold uppercase tracking-widest">
                                Health & Science
                            </div>
                        </div>
                        <h3 className="text-3xl font-bold leading-tight mb-4 group-hover:text-[#A51C30] transition-colors">
                            Researchers identify new mechanism for memory formation
                        </h3>
                        <p className="font-sans text-gray-600 leading-relaxed text-lg">
                            Groundbreaking study from Harvard Medical School illuminates how neurons encode long-term spatial memories.
                        </p>
                    </div>

                    <div className="flex flex-col gap-10">
                        {[
                            { tag: "Arts & Humanities", title: "A modern look at ancient philosophy", img: "https://images.unsplash.com/photo-1544928147-79a79476e6a3?q=80&w=2000&auto=format&fit=crop" },
                            { tag: "Campus & Community", title: "Class of 2026 convocation address", img: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop" },
                            { tag: "National Affairs", title: "Policy experts discuss economic outlook", img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=2664&auto=format&fit=crop" }
                        ].map((item, i) => (
                            <div key={i} className="flex gap-6 group cursor-pointer">
                                <div className="w-24 h-24 shrink-0 overflow-hidden">
                                     <img src={item.img} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" alt="Thumbnail" />
                                </div>
                                <div>
                                    <div className="font-sans text-xs font-bold uppercase text-[#A51C30] mb-2">{item.tag}</div>
                                    <h4 className="text-lg font-bold leading-snug group-hover:text-[#A51C30] transition-colors">{item.title}</h4>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Schools Grid */}
            <section className="bg-[#EBEBE9] py-24 px-6 md:px-12">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-3xl font-serif font-bold mb-12 text-[#1e1e1e]">Schools</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-gray-300 border border-gray-300">
                        {[
                            "College", "Business", "Dental", "Design", 
                            "Divinity", "Education", "Engineering", "Kennedy", 
                            "Law", "Medical", "Public Health", "Radcliffe"
                        ].map((school, i) => (
                            <a key={i} href="#" className="bg-[#F8F7F5] p-6 hover:bg-white transition-colors flex flex-col items-center text-center justify-center aspect-square group">
                                <Landmark size={24} className="text-[#A51C30] mb-4 opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:-translate-y-2" />
                                <span className="font-serif font-bold text-lg">{school}</span>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#1e1e1e] text-white py-20 px-6 border-t-[8px] border-[#A51C30]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                    <div className="col-span-1 md:col-span-2">
                        <div className="flex items-center gap-3 mb-8">
                             <div className="w-8 h-8 border border-white/30 flex items-center justify-center">
                                 <span className="font-serif font-bold text-lg">H</span>
                             </div>
                             <span className="font-serif text-2xl font-bold">{schoolName}</span>
                        </div>
                        <div className="font-sans text-sm text-gray-400 space-y-1">
                            <p>Massachusetts Hall</p>
                            <p>Cambridge, MA 02138</p>
                            <p>Tel: (617) 495-1000</p>
                        </div>
                    </div>
                    
                    <div>
                         <h5 className="font-sans text-xs font-bold uppercase tracking-widest text-[#A51C30] mb-6">Security & Privacy</h5>
                         <ul className="space-y-4 font-sans text-sm text-gray-400">
                             <li><a href="#" className="hover:text-white">Accessibility</a></li>
                             <li><a href="#" className="hover:text-white">Privacy Statement</a></li>
                             <li><a href="#" className="hover:text-white">Trademark Notice</a></li>
                         </ul>
                    </div>

                    <div>
                         <h5 className="font-sans text-xs font-bold uppercase tracking-widest text-[#A51C30] mb-6">Connect</h5>
                         <div className="flex gap-4">
                              <div className="w-10 h-10 bg-white/10 hover:bg-[#A51C30] transition-colors flex items-center justify-center rounded-sm">
                                  <Globe size={18} />
                              </div>
                              <div className="w-10 h-10 bg-white/10 hover:bg-[#A51C30] transition-colors flex items-center justify-center rounded-sm">
                                  <PenTool size={18} />
                              </div>
                         </div>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-white/10 text-center font-sans text-xs text-gray-500">
                    © 2026 The President and Fellows of {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default HarvardUniversity;
